<template>
  <div class="overflow-x-auto rounded-2xl border border-ink/10 bg-white/70">
    <table class="min-w-full text-left text-sm" data-testid="career-table">
      <thead
        class="bg-white/90 text-xs uppercase tracking-[0.2em] text-slate-500"
      >
        <tr>
          <th class="px-4 py-3">{{ t('career-table.header-season') }}</th>
          <th class="px-4 py-3">{{ t('career-table.header-club') }}</th>
          <th class="px-4 py-3">{{ t('career-table.header-games-goals') }}</th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="row in displayRows"
          :key="row.key"
          class="border-t border-ink/10"
          data-testid="career-row"
        >
          <td class="px-4 py-3 font-semibold text-neutral-900">
            {{ row.season }}
          </td>
          <td class="px-4 py-3">
            <div class="flex items-center gap-2">
              <img
                v-if="row.teamLogo"
                :src="row.teamLogo"
                alt=""
                class="h-[1.5em] w-[1.5em] object-contain"
                loading="lazy"
              />
              <span>{{ row.team }}</span>
            </div>
          </td>
          <td class="px-4 py-3">
            {{ formatGamesGoals(row.appearances, row.goals) }}
          </td>
        </tr>
      </tbody>
    </table>
    <div class="flex items-center gap-3 border-t border-ink/10 px-4 py-3">
      <input
        id="aggregateSameClub"
        :checked="isAggregateSameClub"
        type="checkbox"
        class="h-4 w-4 rounded border-slate-300 accent-ink"
        data-testid="aggregate-checkbox"
        @change="onToggleAggregate"
      />
      <label for="aggregateSameClub" class="text-sm font-semibold text-neutral-900">
        {{ t('career-table.aggregate-label') }}
      </label>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import { formatSeason } from "../utils/season";
import { useText } from "../composables/useText.js";

const { t } = useText();

const props = defineProps({
  aggregateSameClub: {
    type: Boolean,
    required: true,
  },
  careerRows: {
    type: Array,
    required: true,
  },
});

const emit = defineEmits(["update:aggregateSameClub"]);

const onToggleAggregate = (event) => {
  emit("update:aggregateSameClub", event.target.checked);
};

const isAggregateSameClub = computed(() => props.aggregateSameClub);

const formatStat = (value) => {
  if (value === null || value === undefined) return "-";
  return value;
};

const formatGamesGoals = (appearances, goals) => {
  return `${formatStat(appearances)} (${formatStat(goals)})`;
};

const formatSeasonRange = (startYear, endYear) => {
  if (startYear === endYear) return formatSeason(startYear);
  const endShort = String((Number(endYear) + 1) % 100).padStart(2, "0");
  return `${startYear}-${endShort}`;
};

const aggregateSeasonRuns = (rows) => {
  const groupedByTeam = new Map();

  rows.forEach((row) => {
    if (!row?.team) return;
    if (!groupedByTeam.has(row.team)) {
      groupedByTeam.set(row.team, { rows: [], logo: row.teamLogo ?? null });
    }
    const entry = groupedByTeam.get(row.team);
    entry.rows.push(row);
    if (!entry.logo && row.teamLogo) {
      entry.logo = row.teamLogo;
    }
  });

  const aggregated = [];

  groupedByTeam.forEach((entry, team) => {
    const sortedRows = [...entry.rows].sort(
      (a, b) => a.seasonYear - b.seasonYear,
    );
    const teamLogo = entry.logo ?? null;
    let startYear = null;
    let endYear = null;
    let appearances = 0;
    let goals = 0;

    const flushRun = () => {
      if (startYear === null || endYear === null) return;
      aggregated.push({
        season: formatSeasonRange(startYear, endYear),
        seasonYear: startYear,
        seasonStart: startYear,
        seasonEnd: endYear,
        team,
        teamLogo,
        appearances,
        goals,
      });
    };

    sortedRows.forEach((row) => {
      const seasonYear = row.seasonYear;
      const rowApps = row.appearances ?? 0;
      const rowGoals = row.goals ?? 0;

      if (startYear === null) {
        startYear = seasonYear;
        endYear = seasonYear;
        appearances = rowApps;
        goals = rowGoals;
        return;
      }

      if (seasonYear === endYear + 1) {
        endYear = seasonYear;
        appearances += rowApps;
        goals += rowGoals;
        return;
      }

      flushRun();
      startYear = seasonYear;
      endYear = seasonYear;
      appearances = rowApps;
      goals = rowGoals;
    });

    flushRun();
  });

  return aggregated.sort((a, b) => {
    if (a.seasonStart !== b.seasonStart) {
      return a.seasonStart - b.seasonStart;
    }
    return a.team.localeCompare(b.team);
  });
};

const displayRows = computed(() => {
  const baseRows = isAggregateSameClub.value
    ? aggregateSeasonRuns(props.careerRows)
    : props.careerRows.map((row) => ({
        ...row,
        seasonStart: row.seasonYear,
        seasonEnd: row.seasonYear,
        teamLogo: row.teamLogo ?? null,
      }));

  return baseRows.map((row) => ({
    ...row,
    key: `${row.team}-${row.seasonStart}-${row.seasonEnd}`,
  }));
});
</script>
